import { z } from 'zod'

// Runtime-validated domain types shared across stores/components. These mirror the backend
// FantasyFootball.Contracts DTOs and the M1-API-CONTRACT. Responses are validated with these
// schemas via the `schema` arg to `http.*`.

export const pingResponseSchema = z.object({
  status: z.string(),
  service: z.string(),
})
export type PingResponse = z.infer<typeof pingResponseSchema>

export const currentUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string().nullable().optional(),
  // M5: gates the admin nav/area in the SPA. Backend (`Policies.SiteAdmin`) remains authoritative.
  isSiteAdmin: z.boolean().optional().default(false),
})
export type CurrentUser = z.infer<typeof currentUserSchema>

// --- Leagues -------------------------------------------------------------------------------

/** Full league detail (GET /api/leagues/{id}, POST /api/leagues). */
export const leagueSchema = z.object({
  id: z.string(),
  name: z.string(),
  season: z.number(),
  teamCount: z.number(),
  commissionerUserId: z.string(),
  createdAt: z.string(),
  // scoring/roster settings are flexible JSON objects (JSONB on the backend); the frontend
  // does not interpret them — the backend owns all rules.
  scoringSettings: z.record(z.string(), z.unknown()),
  rosterSettings: z.record(z.string(), z.unknown()),
  memberCount: z.number(),
  myTeamId: z.string().nullable(),
  // Simulation leagues replay a historical season on a virtual clock (admin tooling).
  isSimulation: z.boolean().optional().default(false),
  virtualNowUtc: z.string().nullable().optional(),
})
export type League = z.infer<typeof leagueSchema>

/** Compact league row for the dashboard list (GET /api/leagues). */
export const leagueSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  season: z.number(),
  teamCount: z.number(),
  memberCount: z.number(),
  myTeamId: z.string().nullable(),
  isSimulation: z.boolean().optional().default(false),
})
export type LeagueSummary = z.infer<typeof leagueSummarySchema>
export const leagueSummaryListSchema = z.array(leagueSummarySchema)

// --- Teams ---------------------------------------------------------------------------------

export const teamSchema = z.object({
  id: z.string(),
  leagueId: z.string(),
  name: z.string(),
  ownerUserId: z.string(),
  ownerDisplayName: z.string().nullable(),
})
export type Team = z.infer<typeof teamSchema>
export const teamListSchema = z.array(teamSchema)

// --- Invites -------------------------------------------------------------------------------

export const inviteStatusSchema = z.enum(['Pending', 'Accepted', 'Revoked', 'Expired'])
export type InviteStatus = z.infer<typeof inviteStatusSchema>

export const inviteSchema = z.object({
  id: z.string(),
  leagueId: z.string(),
  email: z.string(),
  token: z.string(),
  status: inviteStatusSchema,
  expiresAt: z.string(),
  createdAt: z.string(),
})
export type Invite = z.infer<typeof inviteSchema>
export const inviteListSchema = z.array(inviteSchema)

/** Response of POST /api/invites/{token}/accept. */
export const acceptInviteResultSchema = z.object({
  leagueId: z.string(),
})
export type AcceptInviteResult = z.infer<typeof acceptInviteResultSchema>

// --- Players -------------------------------------------------------------------------------

export const playerPositionSchema = z.enum(['QB', 'RB', 'WR', 'TE', 'K', 'DST'])
export type PlayerPosition = z.infer<typeof playerPositionSchema>

export const playerStatusSchema = z.enum(['Active', 'Injured', 'Out', 'Inactive'])
export type PlayerStatus = z.infer<typeof playerStatusSchema>

export const playerSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  position: playerPositionSchema,
  nflTeam: z.string(),
  status: playerStatusSchema,
  jerseyNumber: z.number().nullable(),
  headshotUrl: z.string().nullish(),
  heightInches: z.number().nullish(),
  weightPounds: z.number().nullish(),
})
export type Player = z.infer<typeof playerSchema>

/** Generic paged envelope (PagedResult<T>). Pass the item schema to build a concrete one. */
export function pagedResultSchema<T>(item: z.ZodType<T>) {
  return z.object({
    items: z.array(item),
    page: z.number(),
    pageSize: z.number(),
    total: z.number(),
  })
}
export const pagedPlayersSchema = pagedResultSchema(playerSchema)
export type PagedPlayers = z.infer<typeof pagedPlayersSchema>

// --- Draft (M2) ----------------------------------------------------------------------------
// Mirrors M2-DRAFT-CONTRACT §DTOs. The backend is authoritative for all draft rules; the
// frontend renders state and sends intents.

export const draftStatusSchema = z.enum(['NotStarted', 'InProgress', 'Paused', 'Completed'])
export type DraftStatus = z.infer<typeof draftStatusSchema>

export const draftSettingsSchema = z.object({
  rounds: z.number(),
  pickTimeLimitSeconds: z.number(),
  type: z.literal('Snake'),
  started: z.boolean(),
})
export type DraftSettings = z.infer<typeof draftSettingsSchema>

export const draftPickSchema = z.object({
  overall: z.number(),
  round: z.number(),
  pickInRound: z.number(),
  teamId: z.string(),
  teamName: z.string(),
  playerId: z.string().nullable(),
  player: playerSchema.nullable(),
  madeAt: z.string().nullable(),
  auto: z.boolean(),
})
export type DraftPick = z.infer<typeof draftPickSchema>

export const draftStateSchema = z.object({
  leagueId: z.string(),
  status: draftStatusSchema,
  settings: draftSettingsSchema,
  onClockTeamId: z.string().nullable(),
  onClockOverall: z.number().nullable(),
  pickDeadline: z.string().nullable(),
  currentRound: z.number().nullable(),
  picks: z.array(draftPickSchema),
  myTeamId: z.string().nullable(),
})
export type DraftState = z.infer<typeof draftStateSchema>

export const draftQueueSchema = z.object({
  playerIds: z.array(z.string()),
})
export type DraftQueue = z.infer<typeof draftQueueSchema>

// --- Lineups & scoring (M3) ----------------------------------------------------------------
// Mirrors M3-SCORING-CONTRACT §DTOs. The backend computes all points and validates every
// lineup; the frontend renders this data and sends assignment intents.

/** Roster slot kinds. Starters are everything except BN (bench) and IR. */
export const rosterSlotKindSchema = z.enum(['QB', 'RB', 'WR', 'TE', 'FLEX', 'K', 'DST', 'BN', 'IR'])
export type RosterSlotKind = z.infer<typeof rosterSlotKindSchema>

/** Non-starter slots — excluded from a team's matchup score. */
export const BENCH_SLOTS: ReadonlySet<RosterSlotKind> = new Set(['BN', 'IR'])

export const rosterSlotSchema = z.object({
  slot: rosterSlotKindSchema,
  index: z.number(),
  player: playerSchema.nullable(),
  locked: z.boolean(),
})
export type RosterSlot = z.infer<typeof rosterSlotSchema>

export const rosterSchema = z.object({
  teamId: z.string(),
  teamName: z.string(),
  week: z.number(),
  slots: z.array(rosterSlotSchema),
  bench: z.array(rosterSlotSchema),
})
export type Roster = z.infer<typeof rosterSchema>

/** PUT lineup body item: which rostered player goes in which slot. */
export const lineupAssignmentSchema = z.object({
  playerId: z.string(),
  slot: rosterSlotKindSchema,
})
export type LineupAssignment = z.infer<typeof lineupAssignmentSchema>

export const scoreBreakdownLineSchema = z.object({
  stat: z.string(),
  value: z.number(),
  points: z.number(),
})
export type ScoreBreakdownLine = z.infer<typeof scoreBreakdownLineSchema>

export const scoreBreakdownSchema = z.object({
  points: z.number(),
  lines: z.array(scoreBreakdownLineSchema),
})
export type ScoreBreakdown = z.infer<typeof scoreBreakdownSchema>

export const gameStatusSchema = z.enum(['Scheduled', 'InProgress', 'Final'])
export type GameStatus = z.infer<typeof gameStatusSchema>

// --- Player stat history (player profile page) ---------------------------------------------
// One game's line (week + status + points + per-stat breakdown), grouped into seasons. Points use
// the backend's cached default-settings scoring, so the profile needs no league context.

export const playerGameStatsSchema = z.object({
  week: z.number(),
  gameStatus: gameStatusSchema,
  points: z.number(),
  lines: z.array(scoreBreakdownLineSchema),
})
export type PlayerGameStats = z.infer<typeof playerGameStatsSchema>

export const playerSeasonStatsSchema = z.object({
  season: z.number(),
  gamesPlayed: z.number(),
  totalPoints: z.number(),
  games: z.array(playerGameStatsSchema),
})
export type PlayerSeasonStats = z.infer<typeof playerSeasonStatsSchema>

export const playerStatsSchema = z.object({
  playerId: z.string(),
  seasons: z.array(playerSeasonStatsSchema),
})
export type PlayerStats = z.infer<typeof playerStatsSchema>

export const playerScoreSchema = z.object({
  player: playerSchema,
  slot: rosterSlotKindSchema,
  points: z.number(),
  breakdown: scoreBreakdownSchema,
  gameStatus: gameStatusSchema,
  locked: z.boolean(),
})
export type PlayerScore = z.infer<typeof playerScoreSchema>

export const matchupStatusSchema = z.enum(['Upcoming', 'InProgress', 'Final'])
export type MatchupStatus = z.infer<typeof matchupStatusSchema>

export const matchupSideSchema = z.object({
  teamId: z.string(),
  teamName: z.string(),
  points: z.number(),
  starters: z.array(playerScoreSchema),
})
export type MatchupSide = z.infer<typeof matchupSideSchema>

export const matchupSchema = z.object({
  id: z.string(),
  leagueId: z.string(),
  week: z.number(),
  status: matchupStatusSchema,
  home: matchupSideSchema,
  away: matchupSideSchema,
})
export type Matchup = z.infer<typeof matchupSchema>
export const matchupListSchema = z.array(matchupSchema)

export const standingRowSchema = z.object({
  teamId: z.string(),
  teamName: z.string(),
  wins: z.number(),
  losses: z.number(),
  ties: z.number(),
  pointsFor: z.number(),
  pointsAgainst: z.number(),
  rank: z.number(),
})
export type StandingRow = z.infer<typeof standingRowSchema>
export const standingListSchema = z.array(standingRowSchema)

/** GET /scoreboard envelope: matchups for a week plus the league's current week. */
export const scoreboardSchema = z.object({
  week: z.number(),
  currentWeek: z.number(),
  matchups: z.array(matchupSchema),
})
export type Scoreboard = z.infer<typeof scoreboardSchema>

// --- Free agents, waivers & trades (M4) ----------------------------------------------------
// Mirrors M4-WAIVERS-TRADES-CONTRACT §DTOs. The backend owns every transaction/roster rule; the
// frontend sends intents and surfaces backend error codes. Free agents are PlayerDto[] (the GET
// returns PagedResult<PlayerDto>), so they reuse `playerSchema`/`pagedPlayersSchema`.

export const waiverStatusSchema = z.enum(['Pending', 'Successful', 'Failed', 'Cancelled'])
export type WaiverStatus = z.infer<typeof waiverStatusSchema>

export const waiverClaimSchema = z.object({
  id: z.string(),
  leagueId: z.string(),
  teamId: z.string(),
  teamName: z.string(),
  addPlayer: playerSchema,
  dropPlayer: playerSchema.nullable(),
  priority: z.number(),
  status: waiverStatusSchema,
  processAt: z.string(),
  createdAt: z.string(),
  failureReason: z.string().nullable(),
})
export type WaiverClaim = z.infer<typeof waiverClaimSchema>
export const waiverClaimListSchema = z.array(waiverClaimSchema)

export const addDropResultSchema = z.object({
  added: playerSchema,
  dropped: playerSchema.nullable(),
  teamId: z.string(),
})
export type AddDropResult = z.infer<typeof addDropResultSchema>

export const tradeStatusSchema = z.enum([
  'Proposed',
  'Accepted',
  'Rejected',
  'Cancelled',
  'Expired',
  'Vetoed',
])
export type TradeStatus = z.infer<typeof tradeStatusSchema>

/** TradeItemDto — a single player moving from a team in a trade. */
export const tradeItemSchema = z.object({
  playerId: z.string(),
  player: playerSchema,
  fromTeamId: z.string(),
})
export type TradeItem = z.infer<typeof tradeItemSchema>

export const tradeSchema = z.object({
  id: z.string(),
  leagueId: z.string(),
  proposingTeamId: z.string(),
  proposingTeamName: z.string(),
  receivingTeamId: z.string(),
  receivingTeamName: z.string(),
  offered: z.array(playerSchema),
  requested: z.array(playerSchema),
  status: tradeStatusSchema,
  expiresAt: z.string(),
  createdAt: z.string(),
  respondedAt: z.string().nullable(),
})
export type Trade = z.infer<typeof tradeSchema>
export const tradeListSchema = z.array(tradeSchema)

export const transactionTypeSchema = z.enum(['AddDrop', 'WaiverClaim', 'Trade', 'DraftPick'])
export type TransactionType = z.infer<typeof transactionTypeSchema>

export const transactionSchema = z.object({
  id: z.string(),
  leagueId: z.string(),
  type: transactionTypeSchema,
  summary: z.string(),
  teamId: z.string().nullable(),
  occurredAt: z.string(),
})
export type Transaction = z.infer<typeof transactionSchema>
export const pagedTransactionsSchema = pagedResultSchema(transactionSchema)
export type PagedTransactions = z.infer<typeof pagedTransactionsSchema>

/**
 * Live notification surfaced over `/hubs/notifications` (`Notification` event), persisted server
 * side. The frontend renders the list + unread count; it does not interpret `kind`.
 */
export const notificationSchema = z.object({
  id: z.string(),
  kind: z.string(),
  message: z.string(),
  occurredAt: z.string(),
})
export type Notification = z.infer<typeof notificationSchema>

// --- Commissioner & site admin (M5) --------------------------------------------------------
// Mirrors M5-COMMISSIONER-ADMIN-CONTRACT §DTOs. The backend owns all permissions and rules; the
// frontend gates UI for UX only, sends intents, and surfaces backend error codes (e.g.
// `settings-locked-after-draft`). Settings sub-objects are flexible JSON (JSONB on the backend),
// rendered as structured forms without the frontend interpreting their rules.

/** GET/PUT /commissioner/settings. The four *Settings objects are open JSON maps. */
export const leagueSettingsSchema = z.object({
  name: z.string(),
  teamCount: z.number(),
  scoringSettings: z.record(z.string(), z.unknown()),
  rosterSettings: z.record(z.string(), z.unknown()),
  waiverSettings: z.record(z.string(), z.unknown()),
  draftSettings: z.record(z.string(), z.unknown()),
  // Null before the season starts (e.g. a freshly created league); a number once it has.
  currentWeek: z.number().nullable(),
  seasonWeeks: z.number(),
  locked: z.boolean(),
})
export type LeagueSettings = z.infer<typeof leagueSettingsSchema>

export const memberRoleSchema = z.enum(['Member', 'Commissioner'])
export type MemberRole = z.infer<typeof memberRoleSchema>

export const memberSchema = z.object({
  userId: z.string(),
  displayName: z.string().nullable(),
  email: z.string(),
  role: memberRoleSchema,
  teamId: z.string().nullable(),
  teamName: z.string().nullable(),
})
export type Member = z.infer<typeof memberSchema>
export const memberListSchema = z.array(memberSchema)

export const auditEntrySchema = z.object({
  id: z.string(),
  leagueId: z.string(),
  actorUserId: z.string(),
  actorName: z.string(),
  action: z.string(),
  detail: z.record(z.string(), z.unknown()),
  occurredAt: z.string(),
})
export type AuditEntry = z.infer<typeof auditEntrySchema>
export const pagedAuditSchema = pagedResultSchema(auditEntrySchema)
export type PagedAudit = z.infer<typeof pagedAuditSchema>

/** Response of the week advance/rollback controls. */
export const currentWeekSchema = z.object({
  currentWeek: z.number(),
})
export type CurrentWeek = z.infer<typeof currentWeekSchema>

export const adminUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string().nullable(),
  disabled: z.boolean(),
  isSiteAdmin: z.boolean(),
  leagueCount: z.number(),
  createdAt: z.string(),
})
export type AdminUser = z.infer<typeof adminUserSchema>
export const pagedAdminUsersSchema = pagedResultSchema(adminUserSchema)
export type PagedAdminUsers = z.infer<typeof pagedAdminUsersSchema>

export const adminLeagueSchema = z.object({
  id: z.string(),
  name: z.string(),
  season: z.number(),
  commissionerName: z.string(),
  memberCount: z.number(),
  createdAt: z.string(),
})
export type AdminLeague = z.infer<typeof adminLeagueSchema>
export const pagedAdminLeaguesSchema = pagedResultSchema(adminLeagueSchema)
export type PagedAdminLeagues = z.infer<typeof pagedAdminLeaguesSchema>

export const adminStatsSchema = z.object({
  users: z.number(),
  leagues: z.number(),
  teams: z.number(),
  activeDrafts: z.number(),
})
export type AdminStats = z.infer<typeof adminStatsSchema>
